import Book from "../book";
import Url from "../utils/url";
import request from "./utils/request";
import Path from "../utils/path";
import DisplayOptions from "../displayoptions";
import Resources from "../resources";
import {extend, uuid} from "../utils/core";

import ExNavigation from "./navigation";
import ExContainer from "./container";
import ExPackaging from "./packaging";
import ExRendition from "./rendition";
import ExSpine from "./spine";
import ExEpubCFI from "./epubcfi";
import ExLocations from "./locations";
import ExPageList from "./pagelist";

const CONTAINER_PATH = "META-INF/container.xml";
const IBOOKS_DISPLAY_OPTIONS_PATH = "META-INF/com.apple.ibooks.display-options.xml";

const INPUT_TYPE = {
	BINARY: "binary",
	BASE64: "base64",
	EPUB: "epub",
	OPF: "opf",
	MANIFEST: "json",
	DIRECTORY: "directory"
};

class ExBook extends Book{
	constructor(url, options){
		super(url, options);

		//加入 scrollBarType 預設 extra 設定, 為 spine, 另外 mix / toc 為預做功能
		this.settings = extend(this.settings || {}, {
			scrollBarType: 'spine'
		});
		extend(this.settings, options);

		//用 ExSpine 取代 Spine
		this.spine = new ExSpine(this);

		//用 ExLocations 取代 Locations
		this.locations = new ExLocations(this.spine, this.load.bind(this));

		//scrollBarNodeList ( web scrollbar 節點)
		this.scrollBarNodeList =[];

		//scrollBarNodeHrefToScrollPo ( scrollbar 節點 轉 scrollbar 位置 )
		this.scrollBarNodeHrefToScrollPo = {};

		//tocDispalyHerfToLabel ( toc 連結 轉 名稱 )
		this.tocDispalyHerfToLabel = {};

		//hrefToSpineIndexList ( href 轉 spine index )
		this.hrefToSpineIndexList = {};

		//建立具名固定任務型顯示器Deferred
		this.createDeferreds = {};

		//任務型顯示器
		this.taskRenditions = {};

		//目前最下層背景容器深度
		this._lastBgContainerZIndex = -100;

		//blob暫存
		this._blobCache = {};

		//直橫書模板資訊
		this.templateInfo = {
			isTemplate:false,
			defaultClass:null,
			rootElement:[]
		};
	}

	//固定版cpct轉換為正確頁數
	fixedlayoutCpctToPage(cpct){
		let pageNum = null;
		let epubcfi = new ExEpubCFI();
		if(epubcfi.isCpctString(cpct)){
			let spineIndex = parseInt(epubcfi.getSpineIndexFromCpct(cpct),10);
			pageNum = this.spine.spineByLinearYesIndex.indexOf(spineIndex);
			pageNum = (pageNum < 0) ? null : pageNum + 1;
		}
		return pageNum;
	}

	updateBlobCache(link, blobLink){
		this._blobCache[link] = blobLink;
	}

	getBlobCache(link){
		let blobLink = null;
		if(this._blobCache[link]){
			blobLink = this._blobCache[link];
		}
		return blobLink;
	}

	/**
	 * Open a epub or url
	 * @param {string | ArrayBuffer} input Url, Path or ArrayBuffer
	 * @param {string} [what="binary", "base64", "epub", "opf", "json", "directory"] force opening as a certain type
	 * @returns {Promise} of when the book has been loaded
	 * @example book.open("/path/to/book.epub")
	 */
	open(input, what) {
        
		this.request = request;

		var opening;
		var type = what || this.determineType(input);

		if (type === INPUT_TYPE.BINARY) {
			this.archived = true;
			this.url = new Url("/", "");
			opening = this.openEpub(input);
		} else if (type === INPUT_TYPE.BASE64) {
			this.archived = true;
			this.url = new Url("/", "");
			opening = this.openEpub(input, type);
		} else if (type === INPUT_TYPE.EPUB) {
			this.archived = true;
			this.url = new Url("/", "");
			opening = this.request(input, "binary", this.settings)
				.then(this.openEpub.bind(this));
		} else if(type == INPUT_TYPE.OPF) {
			this.url = new Url(input);
			opening = this.openPackaging(this.url.Path.toString());
		} else if(type == INPUT_TYPE.MANIFEST) {
			this.url = new Url(input);
			opening = this.openManifest(this.url.Path.toString());
		} else {
			this.url = new Url(input);            
			opening = this.openContainer(CONTAINER_PATH)
				.then(this.openPackaging.bind(this));
		}
		return opening;
	}



	/**
	 * Open the epub container
	 * @private
	 * @param  {string} url
	 * @return {string} packagePath
	 */
	openContainer(url) {

		//加入downloadToken參數
		let requestParam = undefined;
		if(this.settings.encryptType  === "enc01"){
			requestParam = {
				downloadToken:this.settings.downloadToken
			};
		}
		
		return this.load(url, requestParam)
			.then((xml) => {
				this.container = new ExContainer(xml, this.settings);
				this.settings.directory = this.container.directory;
				return this.resolve(this.container.packagePath);
			});
	}

	/**
	 * Load Navigation and PageList from package
	 * @private
	 * @param {Packaging} packaging
	 */
	 loadNavigation(packaging) {
		let navPath = packaging.navPath || packaging.ncxPath;
		let toc = packaging.toc;

		// From json manifest
		if (toc) {
			return new Promise((resolve, reject) => {
				this.navigation = new ExNavigation(toc);

				if (packaging.pageList) {
					this.pageList = new ExPageList(packaging.pageList); // TODO: handle page lists from Manifest
				}

				resolve(this.navigation);
			});
		}

		if (!navPath) {
			return new Promise((resolve, reject) => {
				this.navigation = new ExNavigation();
				this.pageList = new ExPageList();

				resolve(this.navigation);
			});
		}
		
		//加入downloadToken參數
		let requestParam = undefined;
		if(this.settings.encryptType  === "enc01"){
			requestParam = {
				downloadToken:this.settings.downloadToken
			};
		}

		return this.load(navPath, requestParam)
			.then((xml) => {
				this.navigation = new ExNavigation(xml);
				this.pageList = new ExPageList(xml);
				return this.navigation;
			});
	}

	/**
	 * Open the Open Packaging Format Xml
	 * @private
	 * @param  {string} url
	 * @return {Promise}
	 */
	openPackaging(url) {
		this.path = new Path(url);

		//加入downloadToken參數
		let requestParam = undefined;
		if(this.settings.encryptType  === "enc01"){
			requestParam = {
				downloadToken:this.settings.downloadToken
			};
		}

		return this.load(url, requestParam)
			.then((xml) => {
				this.packaging = new ExPackaging(xml, this.settings);
				return this.unpack(this.packaging);
			});
	}

	/**
	 * unpack the contents of the Books packaging
	 * @private
	 * @param {Packaging} packaging object
	 */
	 unpack(packaging) {
		this.package = packaging; //TODO: deprecated this

		if (this.packaging.metadata.layout === "") {
			// rendition:layout not set - check display options if book is pre-paginated
			this.load(this.url.resolve(IBOOKS_DISPLAY_OPTIONS_PATH)).then((xml) => {
				this.displayOptions = new DisplayOptions(xml);
				this.loading.displayOptions.resolve(this.displayOptions);
			}).catch((err) => {
				this.displayOptions = new DisplayOptions();
				this.loading.displayOptions.resolve(this.displayOptions);
			});
		} else {
			this.displayOptions = new DisplayOptions();
			this.loading.displayOptions.resolve(this.displayOptions);
		}

		this.spine.unpack(this.packaging, this.resolve.bind(this), this.canonical.bind(this));

		this.resources = new Resources(this.packaging.manifest, {
			archive: this.archive,
			resolver: this.resolve.bind(this),
			request: this.request.bind(this),
			replacements: this.settings.replacements || (this.archived ? "blobUrl" : "base64")
		});

		this.loadNavigation(this.packaging).then(() => {
			// this.toc = this.navigation.toc;

			//加入對 navigation 和 spine 的關聯動作
			this.adjustNavigationAndSpine();
			this.fixNoDisplayLableSpine();
			this.setScrollBarNodeList(this.settings.scrollBarType); //spine, toc, mix
			
			this.loading.navigation.resolve({toc:this.navigation, scrollbar:this.scrollBarNodeList});
		});

		if (this.packaging.coverPath) {
			this.cover = this.resolve(this.packaging.coverPath);
		}
		// Resolve promises
		this.loading.manifest.resolve(this.packaging.manifest);
		this.loading.metadata.resolve(this.packaging.metadata);
		this.loading.spine.resolve(this.spine);
		this.loading.cover.resolve(this.cover);
		this.loading.resources.resolve(this.resources);
		this.loading.pageList.resolve(this.pageList);

		this.isOpen = true;

		if(this.archived || this.settings.replacements && this.settings.replacements != "none") {
			this.replacements().then(() => {
				this.loaded.displayOptions.then(() => {
					this.opening.resolve(this);
				});
			})
				.catch((err) => {
					console.error(err);
				});
		} else {
			// Resolve book opened promise
			this.loaded.displayOptions.then(() => {
				this.opening.resolve(this);
			});
		}

	}

	//加入 toc 的 displayHref (用開頭是否為 ./ ../ / 判斷是否需要做路徑轉換)
	//加入 spine 的 displayLable、tocDisplayHref、tocHref、hash
	adjustNavigationAndSpine(){
		$.each(this.navigation.tocByHref, (target, index)=>{
			let toc = this.navigation.getByIndex(target, index, this.navigation.toc);
			let tocHash = toc.href.split('#')[1];
			let tocRelaySpine = this.spine.get(toc.filename);
			let prefixReg = /^\.*\//g;
			if(prefixReg.test(toc.href)){
				if(tocRelaySpine && tocRelaySpine.url){
					let url = new Url(toc.href, tocRelaySpine.url);
					toc.displayHref = this.path.relative(url.Path.path+url.Url.hash);
				}else{
					toc.displayHref = toc.href;
				}
			}else{
				toc.displayHref = toc.href;
			}
			if(tocRelaySpine){
				toc.linear = tocRelaySpine.linear;
				if(!tocRelaySpine.displayLable){
					tocRelaySpine.displayLable = toc.label;
					tocRelaySpine.tocHref = toc.href;
					tocRelaySpine.tocDisplayHref = toc.displayHref;
				}
				if(tocHash){
					if(!tocRelaySpine.hash){
						tocRelaySpine.hash = [{hash: ""},{hash: tocHash}];
					}else{
						tocRelaySpine.hash.push({hash: tocHash});
					}
				}else{
					if(!tocRelaySpine.hash){
						tocRelaySpine.hash = [{hash: ""}];
					}
				}
			}else{
				toc.linear = false;
			}
		});

	}
	
	//修正無對應 toc 的 spine (最接近章節名稱, 若沒有直接對應toc, 則向前toc取, 直到取得, 若向前已沒有toc則保留空值)
	fixNoDisplayLableSpine(){
		let lastGetDisplayLablspineItem = null;
		$.each(this.spine.spineByLinearYesIndex, (index, spineItemsIndex)=>{
			let spineItem = this.spine.spineItems[spineItemsIndex];
			if(!spineItem.displayLable){
				if(lastGetDisplayLablspineItem){
					let target = lastGetDisplayLablspineItem.tocHref;
					let mostRelayToc =  this.navigation.get(target);
					spineItem.displayLable = (mostRelayToc) ? mostRelayToc.label : "";
				}else{
					spineItem.displayLable = "";
				}
				spineItem.tocDisplayHref = spineItem.href;
			}else{
				lastGetDisplayLablspineItem = spineItem;
			}
		});
	}

	//設定 ScrollBar Node List
	setScrollBarNodeList(type){
		let scrollbarPosition = 1;
		switch(type){
			 case "toc": $.each(this.navigation.tocByHref, (target, index)=>{
								let toc = this.navigation.getByIndex(target, index, this.navigation.toc);
								this.scrollBarNodeList.push({
									displayHref: toc.displayHref, 
									displayLable: toc.label
								});
								this.scrollBarNodeHrefToScrollPo[toc.displayHref] = scrollbarPosition;
								scrollbarPosition += 1;
						 });
						 $.each(this.spine.spineByLinearYesIndex, (index, spineItemsIndex)=>{
							let spineItem = this.spine.spineItems[spineItemsIndex];
							if(this.navigation.tocByFilenameRange[spineItem.filename]){
								let tocIndexItems = this.navigation.tocByFilenameRange[spineItem.filename].items;
								for(const tocItem of tocIndexItems){
									let toc =  this.navigation.getByIndex(tocItem.href, tocItem.index, this.navigation.toc);
									this.tocDispalyHerfToLabel[toc.displayHref] = toc.label;
								}
							}else{
								this.tocDispalyHerfToLabel[spineItem.tocDisplayHref] = spineItem.displayLable;							
							}
						});
			 			 break;
			 case "spine": 	$.each(this.spine.spineByLinearYesIndex, (index, spineItemsIndex)=>{
								let spineItem = this.spine.spineItems[spineItemsIndex];
								this.scrollBarNodeList.push({
									displayHref: spineItem.tocDisplayHref, 
									displayLable: spineItem.displayLable,
									spineItemsIndex: spineItemsIndex
								});
								this.hrefToSpineIndexList[spineItem.tocDisplayHref] = spineItemsIndex;
								this.scrollBarNodeHrefToScrollPo[spineItem.tocDisplayHref] = scrollbarPosition;
								scrollbarPosition += 1;
							});
							$.each(this.spine.spineByLinearYesIndex, (index, spineItemsIndex)=>{
								let spineItem = this.spine.spineItems[spineItemsIndex];
								if(this.navigation.tocByFilenameRange[spineItem.filename]){
									let tocIndexItems = this.navigation.tocByFilenameRange[spineItem.filename].items;
									for(const tocItem of tocIndexItems){
										let toc =  this.navigation.getByIndex(tocItem.href, tocItem.index, this.navigation.toc);
										this.tocDispalyHerfToLabel[toc.displayHref] = toc.label;
									}
								}else{
									this.tocDispalyHerfToLabel[spineItem.tocDisplayHref] = spineItem.displayLable;							
								}
							});
			 				break;
			 case "mix": $.each(this.spine.spineByLinearYesIndex, (index, spineItemsIndex)=>{
							let spineItem = this.spine.spineItems[spineItemsIndex];
							if(this.navigation.tocByFilenameRange[spineItem.filename]){
								let tocIndexItems = this.navigation.tocByFilenameRange[spineItem.filename].items;
								for(const tocItem of tocIndexItems){
									let toc =  this.navigation.getByIndex(tocItem.href, tocItem.index, this.navigation.toc);
									this.scrollBarNodeList.push({
										displayHref: toc.displayHref, 
										displayLable: toc.label
									});
									this.scrollBarNodeHrefToScrollPo[toc.displayHref] = scrollbarPosition;
									scrollbarPosition += 1;
									this.tocDispalyHerfToLabel[toc.displayHref] = toc.label;
								}
							}else{
								this.scrollBarNodeList.push({
									displayHref: spineItem.tocDisplayHref, 
									displayLable: spineItem.displayLable
								});
								this.scrollBarNodeHrefToScrollPo[spineItem.tocDisplayHref] = scrollbarPosition;
								scrollbarPosition += 1;
								this.tocDispalyHerfToLabel[spineItem.tocDisplayHref] = spineItem.displayLable;							
							}
						});
			 			 break;
		}
		
	}

	/**
	 * Load a resource from the Book
	 * @param  {string} path path to the resource to load
	 * @return {Promise}     returns a promise with the requested resource
	 */
	load(path, requestParam) {
		var resolved = this.resolve(path);
		if(this.archived) {
			return this.archive.request(resolved);
		} else {

			//加入requestParam參數
			if(requestParam){
				let {downloadToken, isfault, isDecodefault} = requestParam;
				if(downloadToken){
					let tarUrl = new URL(resolved);
					if(tarUrl.searchParams == undefined){
						let s = [];
						s.push(tarUrl.search);
						s.push("DownloadToken="+downloadToken);
						resolved = (tarUrl.search == "")?s.join("&").replace("&","?"):s.join("&");                
					}else{
						tarUrl.searchParams.append("DownloadToken",downloadToken);
						if(isfault){
							tarUrl.searchParams.append("isfault",isfault);
						}
						if(isDecodefault){
							tarUrl.searchParams.append("isDecodefault",isDecodefault);
						}
						resolved = resolved+tarUrl.search;
					}
				}
			}

			return this.request(resolved, null, this.settings);
		}
	}

	/**
	 * Sugar to render a book to an element
	 * @param  {element | string} element element or string to add a rendition to
	 * @param  {object} [options]
	 * @return {Rendition}
	 */
	 renderTo(element, options) {
		this.rendition = new ExRendition(this, options);
		this.rendition.attachTo(element);

		//指定主要顯示顯示器給 locations
		this.locations.setMainRendition(this.rendition);

		if(options.generateRangeList){
			//加入初始設定
			this._renditionOptions = JSON.parse(JSON.stringify(options));
			this._renditionOptions.generateRangeList = false;
			this._renditionOptions.isHidden = true;
		}

		return this.rendition;
	}

	//建立任務型背後運算顯示器
	createTaskRendition(taskName){
		let createTaskRendition = null;
		if(taskName && this.createDeferreds[taskName]){
			createTaskRendition = this.createDeferreds[taskName];
		}else{
			createTaskRendition = $.Deferred();
			if(taskName){
				this.createDeferreds[taskName] = createTaskRendition;
			}
		
			//加入rendition容器
			let containerId = (taskName) ? `${taskName}` : `epubjs-task-${uuid()}`;
			let element = this.rendition.manager.stage.element;
			this._lastBgContainerZIndex = this._lastBgContainerZIndex-1;
			$("#"+containerId).remove();
			let container = $('<div></div>')[0];
			container.id = containerId;
			container.style.zIndex = this._lastBgContainerZIndex;
			container.style.width = $(element).width()+'px';
			container.style.height = $(element).height()+'px';
			container.style.position = 'fixed';
			container.style.top = 0;
			container.style.left = 0;
			container.style.visibility = 'hidden';
			$("body").append(container);

			//加入rendition
			let options = JSON.parse(JSON.stringify(this.rendition.settings));
			options.generateRangeList = false;
			options.isHidden = true;
			options.taskId = uuid();
			let taskRendition = new ExRendition(this, options);
			taskRendition.started.then(()=>{
				createTaskRendition.resolve(containerId, taskRendition);
			});
			taskRendition.attachTo(container);

			//註冊任務型顯示器
			this.taskRenditions[containerId] = {
				container:container,
				rendition:taskRendition
			}
		}
		return createTaskRendition;
	}

	//移除任務型背後運算顯示器
	removeTaskRendition(containerId){
		if(this.createDeferreds[containerId]){
			delete this.createDeferreds[containerId];
		}
		$("#"+containerId).remove();
		this.taskRenditions[containerId].rendition.destroy();
		this.taskRenditions[containerId] = {
			container:undefined,
			rendition:undefined
		}
		delete this.taskRenditions[containerId];
	}

	//改變背景容器尺寸
	resizeTaskContainer(containerId){
		if(this.taskRenditions[containerId]){
			let element = this.rendition.manager.stage.element;
			this.taskRenditions[containerId].container.style.width = $(element).width()+'px';
			this.taskRenditions[containerId].container.style.height = $(element).height()+'px';
		}
	}

	/**
	 * Find a DOM Range for a given CFI Range
	 * @param  {EpubCFI} cfiRange a epub cfi range
	 * @return {Promise}
	 */
	 getRange(cfiRange) {
		//置換ExEpubCFI
		var cfi = new ExEpubCFI(cfiRange);
		var item = this.spine.get(cfi.spinePos);
		var _request = this.load.bind(this);
		if (!item) {
			return new Promise((resolve, reject) => {
				reject("CFI could not be found");
			});
		}
		return item.load(_request).then(function (contents) {
			var range = cfi.toRange(item.document);
			return range;
		});
	}
}

export default ExBook;